import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon, XMarkIcon, DocumentIcon, TrashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import RichTextEditor from '../../components/RichTextEditor';
import { Dialog } from '@headlessui/react';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import DateTimePickerInput from '../../components/DateTimePickerInput';
import { uploadAttachmentsDirect } from '../../utils/directAttachmentUpload';

// Hardcoded knowledge article categories
const KNOWLEDGE_CATEGORIES = [
  { value: 'guideline', label: 'Guideline' },
  { value: 'known_error', label: 'Known Error' },
  { value: 'kb_document', label: 'KB Document' },
  { value: 'domain_controller_build', label: 'Domain Controller Build' },
  { value: 'mfa_modification', label: 'MFA Modification' },
  { value: 'investigation_steps', label: 'Investigation steps' },
  { value: 'itsm_l2_sop', label: 'ITSM - L2 SOP For Attaching and Initiali' },
  { value: 'sending_encrypted_email', label: 'Sending Encrypted Email' },
  { value: 'windows_updates', label: 'Windows Updates' },
  { value: 'fsmo_migration', label: 'FSMO Migration' }
];

interface Attachment {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedById: string;
  createdAt: string;
  User?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface KnowledgeFormData {
  title: string;
  shortDescription: string;
  content: string;
  category: string;
  knowledgeBaseId: string;
  kbGroupEmail: string;
  validTill: string;
  tags: string[];
  state: string;
  published: boolean;
  version: number;
}

interface KnowledgeBase {
  id: string;
  name: string;
  active: boolean;
}

const articleStates = [
  { value: 'new', label: 'Draft' },
  { value: 'in_review', label: 'In Review' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'published', label: 'Published' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'retired', label: 'Retired' }
];

const KnowledgeEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatDate } = useDateFormatter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const hasPermission = can('knowledge', 'update');
  const { user } = useAuth();

  const [formData, setFormData] = useState<KnowledgeFormData>({
    title: '',
    shortDescription: '',
    content: '',
    category: '',
    knowledgeBaseId: '',
    kbGroupEmail: '',
    validTill: '',
    tags: [],
    state: 'new',
    published: false,
    version: 1
  });
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof KnowledgeFormData | 'submit' | 'attachments', string>>>({});

  // Attachment states
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ isOpen: boolean; attachmentId: string | null; attachmentName: string }>({
    isOpen: false,
    attachmentId: null,
    attachmentName: ''
  });

  // Fetch existing article
  const { data: article, isLoading: isLoadingArticle } = useQuery({
    queryKey: ['knowledge', id],
    queryFn: async () => {
      const response = await api.get(`/knowledge/${id}`);
      return response.data;
    }
  });

  // Set form data when article loads
  useEffect(() => {
    if (article) {
      setFormData({
        title: article.title || article.shortDescription || '',
        shortDescription: article.shortDescription || '',
        content: article.content || '',
        category: article.category || '',
        knowledgeBaseId: article.kbBaseId || article.knowledgeBaseId || '',
        kbGroupEmail: article.kbGroupEmail || '',
        validTill: article.validTill ? new Date(article.validTill).toISOString().split('T')[0] : '',
        tags: article.tags || [],
        state: article.state || 'new',
        published: article.published || false,
        version: article.version || 1
      });
    }
  }, [article]);

  // Fetch knowledge bases for dropdown
  const { data: knowledgeBasesData } = useQuery({
    queryKey: ['knowledge-bases'],
    queryFn: async () => {
      const response = await api.get('/knowledge-base?limit=100&active=true');
      return response.data;
    }
  });

  const knowledgeBases: KnowledgeBase[] = knowledgeBasesData?.knowledgeBases || [];

  // Fetch attachments for this article
  const { data: attachmentsData, refetch: refetchAttachments } = useQuery({
    queryKey: ['knowledge-attachments', id],
    queryFn: async () => {
      const response = await api.get(`/knowledge/${id}/attachments`);
      return response.data;
    },
    enabled: !!id
  });

  const attachments: Attachment[] = attachmentsData?.attachments || [];

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle file upload
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !id) return;

    setIsUploading(true);
    setUploadProgress('Uploading...');

    try {
      await uploadAttachmentsDirect(api, `/knowledge/${id}/attachments`, files);

      toast.success(`Successfully uploaded ${files.length} file(s)`);
      refetchAttachments();
      // Clear attachment error when files are uploaded
      if (errors.attachments) {
        setErrors(prev => ({ ...prev, attachments: undefined }));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to upload files');
    } finally {
      setIsUploading(false);
      setUploadProgress('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileUpload(e.target.files);
  };

  // Handle delete attachment - open confirmation modal
  const handleDeleteAttachment = (attachmentId: string, attachmentName: string) => {
    setDeleteConfirmModal({
      isOpen: true,
      attachmentId,
      attachmentName
    });
  };

  // Confirm delete attachment
  const confirmDeleteAttachment = async () => {
    if (!deleteConfirmModal.attachmentId) return;

    try {
      await api.delete(`/knowledge/${id}/attachments/${deleteConfirmModal.attachmentId}`);
      toast.success('Attachment deleted successfully');
      refetchAttachments();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete attachment');
    } finally {
      setDeleteConfirmModal({ isOpen: false, attachmentId: null, attachmentName: '' });
    }
  };

  // Handle download attachment
  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      const response = await api.get(`/knowledge/${id}/attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error('Failed to download attachment');
    }
  };

  // Get author name from article
  const authorName = article?.assignedTo
    ? `${article.assignedTo.firstName || ''} ${article.assignedTo.lastName || ''}`.trim() || article.assignedTo.email
    : user ? `${(user as any).firstName || ''} ${(user as any).lastName || ''}`.trim() || (user as any).email : '';

  // Check if current user is the Knowledge Base owner
  const isKbOwner = user && article?.kbBase?.owner?.id === (user as any).id;

  // Check if form should be read-only (in_review and user is not KB owner)
  const isReadOnly = article?.state === 'in_review' && !isKbOwner;

  // Mutation for sending article for review
  const sendForReviewMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/knowledge/${id}/send-for-review`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', id] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-article', id] });
      toast.success(data.message || 'Article sent for review successfully');
      navigate(`/knowledge/${id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to send article for review');
    }
  });

  // Mutation for reviewing article (KB owner only)
  const reviewMutation = useMutation({
    mutationFn: async ({ action, comments }: { action: 'reviewed' | 'rejected'; comments?: string }) => {
      const response = await api.post(`/knowledge/${id}/review`, { action, comments });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', id] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-article', id] });
      toast.success(data.message || 'Review completed successfully');
      navigate(`/knowledge/${id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to review article');
    }
  });

  // Mutation for publishing article (KB owner only)
  const publishMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/knowledge/${id}/publish`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', id] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-article', id] });
      toast.success(data.message || 'Article published successfully');
      navigate(`/knowledge/${id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to publish article');
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (data: KnowledgeFormData) => {
      const payload: any = {
        ...data,
        shortDescription: data.title,
        published: data.state === 'published',
        version: data.version + 1
      };
      // Only include kbGroupEmail if it has a value, otherwise set to null
      if (!data.kbGroupEmail) {
        payload.kbGroupEmail = null;
      }
      const response = await api.put(`/knowledge/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', id] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-article', id] });
      toast.success('Article updated successfully');
      navigate(`/knowledge/${id}`);
    },
    onError: (error: any) => {
      if (error.response?.data?.errors) {
        const validationErrors: Partial<Record<keyof KnowledgeFormData, string>> = {};
        error.response.data.errors.forEach((err: any) => {
          if (err.field) {
            validationErrors[err.field as keyof KnowledgeFormData] = err.message;
          }
        });
        setErrors(validationErrors);
      } else {
        setErrors({ submit: error.response?.data?.message || 'Failed to update article' });
      }
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    const newErrors: Partial<Record<keyof KnowledgeFormData | 'attachments', string>> = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    // Validate email if provided
    if (formData.kbGroupEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.kbGroupEmail)) {
      newErrors.kbGroupEmail = 'Please enter a valid email address';
    }
    if (attachments.length === 0) {
      newErrors.attachments = 'At least one attachment is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    updateMutation.mutate(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Inline email validation for kbGroupEmail
    if (name === 'kbGroupEmail') {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setErrors(prev => ({ ...prev, kbGroupEmail: 'Please enter a valid email address' }));
      } else {
        setErrors(prev => ({ ...prev, kbGroupEmail: undefined }));
      }
      return;
    }

    // Clear error for this field
    if (errors[name as keyof KnowledgeFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleContentChange = (value: string) => {
    setFormData(prev => ({ ...prev, content: value }));
    if (errors.content) {
      setErrors(prev => ({ ...prev, content: undefined }));
    }
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = tagInput.trim();
      if (tag && !formData.tags.includes(tag)) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Show loading while checking permissions or loading article
  if (permissionsLoading || isLoadingArticle) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to edit knowledge articles.
        </div>
      </div>
    );
  }

  // Get category label for display
  const getCategoryLabel = (value: string) => {
    const cat = KNOWLEDGE_CATEGORIES.find(c => c.value === value);
    return cat?.label || value;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate(`/knowledge/${id}`)}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Article
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Edit Knowledge Article</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Update the knowledge article details
                </p>
              </div>
              <div className="text-sm text-gray-500">
                Version {formData.version}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {errors.submit && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            )}

            {/* Row 1: Title (full width) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-1">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 pt-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-11">
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter article title"
                      className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.title ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                      }`}
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Category & State */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                      Category <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.category ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select Category</option>
                      {KNOWLEDGE_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                    {errors.category && (
                      <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                      State
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={articleStates.find(s => s.value === formData.state)?.label || 'Draft'}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Knowledge Base & KB Group Email */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label htmlFor="knowledgeBaseId" className="block text-sm font-medium text-gray-700">
                      Knowledge Base
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="knowledgeBaseId"
                      name="knowledgeBaseId"
                      value={formData.knowledgeBaseId}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="">Select Knowledge Base</option>
                      {knowledgeBases.map(kb => (
                        <option key={kb.id} value={kb.id}>{kb.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label htmlFor="kbGroupEmail" className="block text-sm font-medium text-gray-700">
                      KB Group Email
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="email"
                      id="kbGroupEmail"
                      name="kbGroupEmail"
                      value={formData.kbGroupEmail}
                      onChange={handleChange}
                      placeholder="Enter group email (optional)"
                      className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.kbGroupEmail ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                      }`}
                    />
                    {errors.kbGroupEmail && (
                      <p className="mt-1 text-sm text-red-600">{errors.kbGroupEmail}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Author & Valid Till */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label htmlFor="author" className="block text-sm font-medium text-gray-700">
                      Author
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      id="author"
                      value={authorName}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label htmlFor="validTill" className="block text-sm font-medium text-gray-700">
                      Valid Till
                    </label>
                  </div>
                  <div className="col-span-9">
                    <DateTimePickerInput
                      id="validTill"
                      name="validTill"
                      value={formData.validTill}
                      onChange={(val) => setFormData(prev => ({ ...prev, validTill: val }))}
                      showTimeSelect={false}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 5: Attachments */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 pt-2">
                      Attachments <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-11">
                    {/* Upload area */}
                    <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
                      isUploading ? 'border-blue-400 bg-blue-50' :
                      errors.attachments ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}>
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        id="attachments"
                        ref={fileInputRef}
                        onChange={handleFileInputChange}
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.txt,.xlsx,.xls,.csv"
                        disabled={isUploading}
                      />
                      <label
                        htmlFor="attachments"
                        className={`cursor-pointer text-sm ${isUploading ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'}`}
                      >
                        {isUploading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {uploadProgress}
                          </span>
                        ) : (
                          <>
                            <span className="text-blue-600 hover:text-blue-700">Click to upload</span> or drag and drop
                            <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, PNG, JPG up to 25MB</p>
                          </>
                        )}
                      </label>
                    </div>
                    {errors.attachments && (
                      <p className="mt-1 text-sm text-red-600">{errors.attachments}</p>
                    )}

                    {/* Existing attachments list */}
                    {attachments.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-sm font-medium text-gray-700">Uploaded Files ({attachments.length})</p>
                        <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                          {attachments.map((attachment) => (
                            <div key={attachment.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <DocumentIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{attachment.originalName}</p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(attachment.size)} - {formatDate(attachment.createdAt)}
                                    {attachment.User && ` by ${attachment.User.firstName} ${attachment.User.lastName}`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handleDownloadAttachment(attachment)}
                                  className="p-1.5 text-gray-400 hover:text-blue-600 rounded-md hover:bg-gray-100"
                                  title="Download"
                                >
                                  <ArrowDownTrayIcon className="h-5 w-5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAttachment(attachment.id, attachment.originalName)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-100"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 6: Keywords/Tags */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 pt-2">
                      Keywords
                    </label>
                  </div>
                  <div className="col-span-11">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleAddTag}
                      placeholder="Type a keyword and press Enter"
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Press Enter or comma to add a keyword
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 7: Content (Rich Text) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 pt-2">
                      Content
                    </label>
                  </div>
                  <div className="col-span-11">
                    <RichTextEditor
                      value={formData.content}
                      onChange={handleContentChange}
                      placeholder="Write your article content here..."
                      minHeight="250px"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              {/* Read-only notice for in_review state */}
              {isReadOnly && (
                <div className="flex items-center text-amber-600 text-sm">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  This article is currently in review and cannot be edited.
                </div>
              )}
              {!isReadOnly && <div />}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`/knowledge/${id}`)}
                  className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  Cancel
                </button>

                {/* Update button - only show if not in read-only mode */}
                {!isReadOnly && (
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {updateMutation.isPending ? 'Updating...' : 'Update Article'}
                  </button>
                )}

                {/* Send for Review button - show for non-owners when article is in draft/new/rejected state */}
                {!isKbOwner && (article?.state === 'new' || article?.state === 'rejected') && (
                  <button
                    type="button"
                    onClick={() => sendForReviewMutation.mutate()}
                    disabled={sendForReviewMutation.isPending || attachments.length === 0}
                    title={attachments.length === 0 ? 'At least one attachment is required to send for review' : ''}
                    className="px-6 py-2 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {sendForReviewMutation.isPending ? 'Sending...' : 'Send for Review'}
                  </button>
                )}

                {/* Review actions - only for KB owner when article is in_review */}
                {isKbOwner && article?.state === 'in_review' && (
                  <>
                    <button
                      type="button"
                      onClick={() => reviewMutation.mutate({ action: 'rejected' })}
                      disabled={reviewMutation.isPending}
                      className="px-6 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {reviewMutation.isPending ? 'Processing...' : 'Reject'}
                    </button>
                    <button
                      type="button"
                      onClick={() => reviewMutation.mutate({ action: 'reviewed' })}
                      disabled={reviewMutation.isPending}
                      className="px-6 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {reviewMutation.isPending ? 'Processing...' : 'Approve'}
                    </button>
                  </>
                )}

                {/* Publish button - only for KB owner when article is reviewed */}
                {isKbOwner && article?.state === 'reviewed' && (
                  <button
                    type="button"
                    onClick={() => publishMutation.mutate()}
                    disabled={publishMutation.isPending}
                    className="px-6 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {publishMutation.isPending ? 'Publishing...' : 'Publish'}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Delete Attachment Confirmation Modal */}
      <Dialog
        open={deleteConfirmModal.isOpen}
        onClose={() => setDeleteConfirmModal({ isOpen: false, attachmentId: null, attachmentName: '' })}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm w-full bg-white rounded-lg shadow-xl">
            <div className="px-4 py-3 border-b border-gray-200">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Delete Attachment
              </Dialog.Title>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete the attachment{' '}
                <span className="font-medium text-gray-900">"{deleteConfirmModal.attachmentName}"</span>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This action cannot be undone.
              </p>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmModal({ isOpen: false, attachmentId: null, attachmentName: '' })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteAttachment}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default KnowledgeEdit;
